const { CarolineLibrarySessionError } = require('./storeErrors');
const { carolineLibraryClient } = require('./client');
const { createCarolineGuestLibraryStorage } = require('./guestStorage');
const libraryKey = require('./libraryKey');

const freezeReference = (reference) => Object.freeze({
    metaId: reference.metaId,
    mediaType: reference.mediaType,
    addedAt: reference.addedAt,
});

const makeSnapshot = (status, userId, itemMap, error = null) => Object.freeze({
    status,
    userId,
    items: Object.freeze(Array.from(itemMap.values(), freezeReference)),
    error,
});

const normalizeInput = ({ metaId, mediaType }) => {
    if (typeof metaId !== 'string' || typeof mediaType !== 'string') {
        throw new TypeError('Caroline Library references require mediaType and metaId strings.');
    }
    const normalizedMetaId = metaId.trim();
    const normalizedMediaType = mediaType.trim();
    if (normalizedMetaId.length === 0 || normalizedMediaType.length === 0) {
        throw new TypeError('Caroline Library references cannot be empty.');
    }
    return { metaId: normalizedMetaId, mediaType: normalizedMediaType };
};

const isUnavailableError = (error) => error?.status === 503
    || error?.name === 'TypeError'
    || error?.code === 'NETWORK_ERROR';

const createCarolineLibraryStore = (client = carolineLibraryClient, guestStorage = createCarolineGuestLibraryStorage()) => {
    const listeners = new Set();
    let snapshot = makeSnapshot('idle', null, new Map());
    let sessionStatus = 'loading';
    let activeUserId = null;
    let generation = 0;
    let loadSequence = 0;
    let loadPromise = null;
    let hasCompleteSnapshot = false;
    let itemsByKey = new Map();
    let confirmedByKey = new Map();
    let operationTails = new Map();
    let latestIntents = new Map();
    let pendingDesired = new Map();

    const publish = (status = snapshot.status, error = snapshot.error) => {
        snapshot = makeSnapshot(status, activeUserId, itemsByKey, error);
        listeners.forEach((listener) => listener());
        return snapshot;
    };

    const isCurrentSession = (userId, expectedGeneration) => sessionStatus === 'authenticated'
        && activeUserId === userId
        && generation === expectedGeneration;

    const isGuestSession = (expectedGeneration = generation) => ['unauthenticated', 'unavailable'].includes(sessionStatus)
        && activeUserId === null
        && generation === expectedGeneration;

    const clearUserState = (nextSessionStatus, nextLibraryStatus) => {
        generation += 1;
        sessionStatus = nextSessionStatus;
        activeUserId = null;
        loadPromise = null;
        hasCompleteSnapshot = false;
        itemsByKey = new Map();
        confirmedByKey = new Map();
        operationTails = new Map();
        latestIntents = new Map();
        pendingDesired = new Map();
        publish(nextLibraryStatus, null);
    };

    const startLoad = (userId, expectedGeneration) => {
        const requestSequence = ++loadSequence;
        publish('loading', null);
        let requestPromise;
        requestPromise = Promise.resolve()
            .then(() => client.getLibrary())
            .then((items) => {
                if (!isCurrentSession(userId, expectedGeneration) || requestSequence !== loadSequence) return [];
                const nextItems = new Map();
                items.forEach((item) => nextItems.set(libraryKey(item.mediaType, item.metaId), freezeReference(item)));
                itemsByKey = nextItems;
                confirmedByKey = new Map(nextItems);
                hasCompleteSnapshot = true;
                publish('ready', null);
                return snapshot.items;
            })
            .catch((error) => {
                if (isCurrentSession(userId, expectedGeneration) && requestSequence === loadSequence) {
                    publish(isUnavailableError(error) ? 'unavailable' : 'error', error instanceof Error ? error : new Error('Caroline Library could not be loaded.'));
                }
                throw error;
            });
        loadPromise = requestPromise;
        requestPromise.then(() => {
            if (loadPromise === requestPromise) loadPromise = null;
        }, () => {
            if (loadPromise === requestPromise) loadPromise = null;
        });
        return requestPromise;
    };

    const enterGuestSession = (nextSessionStatus, forceReload = false) => {
        if (!forceReload && sessionStatus === nextSessionStatus && activeUserId === null && hasCompleteSnapshot) {
            return Promise.resolve(snapshot.items);
        }

        generation += 1;
        sessionStatus = nextSessionStatus;
        activeUserId = null;
        loadPromise = null;
        hasCompleteSnapshot = true;
        itemsByKey = new Map();
        confirmedByKey = new Map();
        operationTails = new Map();
        latestIntents = new Map();
        pendingDesired = new Map();
        guestStorage.read().forEach((item) => {
            const reference = freezeReference(item);
            itemsByKey.set(libraryKey(reference.mediaType, reference.metaId), reference);
        });
        confirmedByKey = new Map(itemsByKey);
        publish('ready', null);
        return Promise.resolve(snapshot.items);
    };

    const setSession = (session) => {
        const status = session?.status;
        if (status !== 'authenticated' || session?.isAuthenticated !== true) {
            const nextSessionStatus = ['loading', 'unauthenticated', 'unavailable'].includes(status) ? status : 'unavailable';
            if (nextSessionStatus === 'unauthenticated' || nextSessionStatus === 'unavailable') {
                return enterGuestSession(nextSessionStatus);
            }
            const nextLibraryStatus = nextSessionStatus;
            if (sessionStatus !== nextSessionStatus || activeUserId !== null) {
                clearUserState(nextSessionStatus, nextLibraryStatus);
            } else if (snapshot.status !== nextLibraryStatus || snapshot.items.length > 0) {
                itemsByKey = new Map();
                confirmedByKey = new Map();
                hasCompleteSnapshot = false;
                publish(nextLibraryStatus, null);
            }
            return Promise.resolve(snapshot.items);
        }

        const userId = session?.user?.id;
        if (typeof userId !== 'string' || userId.trim().length === 0) {
            clearUserState('unavailable', 'unavailable');
            return Promise.resolve(snapshot.items);
        }

        if (sessionStatus === 'authenticated' && activeUserId === userId) {
            return loadPromise || Promise.resolve(snapshot.items);
        }

        generation += 1;
        sessionStatus = 'authenticated';
        activeUserId = userId;
        hasCompleteSnapshot = false;
        itemsByKey = new Map();
        confirmedByKey = new Map();
        operationTails = new Map();
        latestIntents = new Map();
        pendingDesired = new Map();
        const currentGeneration = generation;
        publish('loading', null);
        const requestPromise = startLoad(userId, currentGeneration);
        // Session observation is a fire-and-forget boundary; status/error are stored for the hook.
        requestPromise.catch(() => undefined);
        return requestPromise;
    };

    const waitForInitialLoad = async (userId, expectedGeneration) => {
        if (!isCurrentSession(userId, expectedGeneration)) {
            throw new CarolineLibrarySessionError('The Caroline session changed before the operation started.');
        }
        const pendingLoad = loadPromise;
        if (pendingLoad) {
            try {
                await pendingLoad;
            } catch {
                // Membership writes remain possible after a read error; their result is still explicit.
            }
        }
        if (!isCurrentSession(userId, expectedGeneration)) {
            throw new CarolineLibrarySessionError('The Caroline session changed before the operation started.');
        }
    };

    const enqueueMembership = async (input, desired, expectedUserId) => {
        const reference = normalizeInput(input);
        if (expectedUserId === null && isGuestSession()) {
            const key = libraryKey(reference.mediaType, reference.metaId);
            const current = itemsByKey.get(key);
            if (desired && !current) {
                itemsByKey.set(key, freezeReference({ ...reference, addedAt: new Date().toISOString() }));
            } else if (!desired && current) {
                itemsByKey.delete(key);
            } else {
                return desired ? current : undefined;
            }
            confirmedByKey = new Map(itemsByKey);
            guestStorage.write(Array.from(itemsByKey.values()));
            publish('ready', null);
            return desired ? itemsByKey.get(key) : undefined;
        }
        if (sessionStatus !== 'authenticated' || activeUserId !== expectedUserId) {
            throw new CarolineLibrarySessionError('A Caroline session is required for Library changes.');
        }
        const expectedGeneration = generation;
        if (loadPromise) {
            await waitForInitialLoad(expectedUserId, expectedGeneration);
        }

        if (!isCurrentSession(expectedUserId, expectedGeneration)) {
            throw new CarolineLibrarySessionError('The Caroline session changed before the operation started.');
        }

        const key = libraryKey(reference.mediaType, reference.metaId);
        const pending = pendingDesired.get(key);
        if (pending?.desired === desired) return pending.promise;

        if (!pending && hasCompleteSnapshot && snapshot.status === 'ready') {
            const currentlyPresent = confirmedByKey.has(key);
            if (currentlyPresent === desired) {
                return desired ? confirmedByKey.get(key) : undefined;
            }
        }

        const optimisticReference = desired
            ? freezeReference({ ...reference, addedAt: new Date().toISOString() })
            : null;
        if (desired) itemsByKey.set(key, optimisticReference);
        else itemsByKey.delete(key);
        publish(snapshot.status, snapshot.error);

        const prior = operationTails.get(key) || Promise.resolve();
        const previousIntent = latestIntents.get(key);
        const version = (previousIntent?.version || 0) + 1;
        latestIntents.set(key, { version, desired });

        let operation;
        operation = prior.catch(() => undefined).then(async () => {
            if (!isCurrentSession(expectedUserId, expectedGeneration)) return undefined;
            if (latestIntents.get(key)?.version !== version) return undefined;

            try {
                let persistedReference;
                if (desired) {
                    persistedReference = await client.addLibraryItem(reference);
                } else {
                    await client.removeLibraryItem(reference);
                }

                if (!isCurrentSession(expectedUserId, expectedGeneration)) return undefined;
                if (desired) confirmedByKey.set(key, freezeReference(persistedReference));
                else confirmedByKey.delete(key);

                if (latestIntents.get(key)?.version === version) {
                    if (desired) itemsByKey.set(key, freezeReference(persistedReference));
                    else itemsByKey.delete(key);
                    publish(snapshot.status, snapshot.error);
                }
                return desired ? freezeReference(persistedReference) : undefined;
            } catch (error) {
                if (isCurrentSession(expectedUserId, expectedGeneration)
                    && latestIntents.get(key)?.version === version) {
                    if (confirmedByKey.has(key)) itemsByKey.set(key, confirmedByKey.get(key));
                    else itemsByKey.delete(key);
                    const nextStatus = isUnavailableError(error) ? 'unavailable' : 'error';
                    publish(nextStatus, error instanceof Error ? error : new Error('Caroline Library update failed.'));
                }
                throw error;
            }
        });

        operationTails.set(key, operation);
        pendingDesired.set(key, { desired, promise: operation });
        const cleanup = () => {
            if (operationTails.get(key) === operation) operationTails.delete(key);
            if (pendingDesired.get(key)?.promise === operation) pendingDesired.delete(key);
        };
        operation.then(cleanup, cleanup);
        return operation;
    };

    const refresh = async (expectedUserId = activeUserId) => {
        if (expectedUserId === null && isGuestSession()) {
            return enterGuestSession(sessionStatus, true);
        }
        if (typeof expectedUserId !== 'string'
            || sessionStatus !== 'authenticated'
            || activeUserId !== expectedUserId) {
            return [];
        }
        const expectedGeneration = generation;
        const activeOperations = Array.from(operationTails.values());
        if (activeOperations.length > 0) await Promise.allSettled(activeOperations);
        if (!isCurrentSession(expectedUserId, expectedGeneration)) return [];
        if (loadPromise) {
            try {
                await loadPromise;
            } catch {
                // Continue with an explicit refresh attempt.
            }
            if (!isCurrentSession(expectedUserId, expectedGeneration)) return [];
        }
        return startLoad(expectedUserId, expectedGeneration);
    };

    const isInLibrary = (mediaType, metaId, expectedUserId = activeUserId) => {
        if (expectedUserId === null && isGuestSession()) {
            if (snapshot.status !== 'ready' || !hasCompleteSnapshot) return undefined;
            if (typeof mediaType !== 'string' || typeof metaId !== 'string') return undefined;
            return itemsByKey.has(libraryKey(mediaType, metaId));
        }
        if (snapshot.status !== 'ready'
            || !hasCompleteSnapshot
            || sessionStatus !== 'authenticated'
            || activeUserId !== expectedUserId
            || typeof mediaType !== 'string'
            || typeof metaId !== 'string') {
            return undefined;
        }
        return itemsByKey.has(libraryKey(mediaType, metaId));
    };

    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        getSnapshot() {
            return snapshot;
        },
        setSession,
        refresh,
        isInLibrary,
        add(reference, expectedUserId = activeUserId) {
            return enqueueMembership(reference, true, expectedUserId);
        },
        remove(reference, expectedUserId = activeUserId) {
            return enqueueMembership(reference, false, expectedUserId);
        },
    };
};

module.exports = {
    CarolineLibrarySessionError,
    createCarolineLibraryStore,
};
